// event-concurrency-queue.ts
type Task<T> = () => Promise<T>;
type TaskOrPromise<T> = Task<T> | Promise<T>;

function isThenable<T = unknown>(v: any): v is Promise<T> {
  return v && (typeof v === "object" || typeof v === "function") && typeof v.then === "function";
}

export class EventConcurrencyQueue {
  private running = 0;
  private readonly concurrency: number;
  private queue: Array<{
    task: Task<unknown>;
    resolve: (v: unknown) => void;
    reject: (e: unknown) => void;
  }> = [];

  constructor(concurrency: number) {
    if (!Number.isInteger(concurrency) || concurrency < 1) {
      throw new Error("concurrency must be a positive integer");
    }
    this.concurrency = concurrency;
  }

  /**
   * Submit a single task (from an event). Accepts either:
   *  - a function returning a Promise (preferred for start-time control), or
   *  - an already-created Promise (note: it has already started).
   */
  submit<T>(input: TaskOrPromise<T>): Promise<T> {
    const task: Task<T> =
      typeof input === "function"
        ? (input as Task<T>)
        : isThenable<T>(input)
          ? () => input // already-started promise; we only await it when a slot is available
          : () => Promise.resolve(input as unknown as T); // fallback if someone passes a plain value

    return new Promise<T>((resolve, reject) => {
      this.queue.push({ task, resolve, reject });
      this.drain();
    });
  }

  /** Await until nothing is running or queued. Useful for shutdown/flush. */
  async onIdle(): Promise<void> {
    while (this.running > 0 || this.queue.length > 0) {
      await new Promise((r) => setTimeout(r, 5));
    }
  }

  get sizeRunning() { return this.running; }
  get sizeQueued()  { return this.queue.length; }

  private drain() {
    while (this.running < this.concurrency && this.queue.length > 0) {
      const { task, resolve, reject } = this.queue.shift()!;
      this.running++;
      Promise.resolve()
        .then(task)
        .then(resolve, reject)
        .finally(() => {
          this.running--;
          this.drain();
        });
    }
  }
}
